import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type {
  FinancialSnapshotResponse,
  FinancialTransactionFilterOptions,
  FinancialTransactionFilterOptionsParams,
  FinancialTransactionItemData,
  FinancialTransactionItemPatchData,
  FinancialTransactionItemResponse,
  FinancialTransactionItemUpdateData,
  FinancialTransactionItemsParams,
  FinancialTransactionItemsResponse,
  PaymentCreateBody,
  PaymentResponse,
  PaymentUpdateBody
} from "./services.types.ts"

const BASE_URL = `/project-management/financial-transaction-items`

const PATHS = {
  getFinancialTransactionItems: `${BASE_URL}/`,
  exportFinancialTransactionItems: `${BASE_URL}/export/`,
  createFinancialTransactionItem: `${BASE_URL}/`,
  getFinancialTransactionItemsFilterOptions: `${BASE_URL}/filter_options/`,
  getFinancialTransactionItem: (id: string) => `${BASE_URL}/${id}/`,
  updateFinancialTransactionItem: (id: string) => `${BASE_URL}/${id}/`,
  patchFinancialTransactionItem: (id: string) => `${BASE_URL}/${id}/`,
  deleteFinancialTransactionItem: (id: string) => `${BASE_URL}/${id}/`,
  getFinancialSnapshot: (id: string) =>
    `/project-management/projects/${id}/financial_snapshot/`,
  getFinancialAlerts: (id: string) =>
    `/project-management/projects/${id}/financial-alerts/`,
  muteFinancialAlerts: (id: string) =>
    `/project-management/projects/${id}/financial-alerts-mute/`,
  addPayment: (id: string) => `${BASE_URL}/${id}/payments/`,
  paymentDetail: (transactionId: string, paymentId: string) =>
    `${BASE_URL}/${transactionId}/payments/${paymentId}/`
}

export const getFinancialTransactionItems = async (
  params: FinancialTransactionItemsParams
) => {
  return request.api.get<
    FinancialTransactionItemsResponse,
    BaseApiResponse<unknown>
  >(PATHS.getFinancialTransactionItems, params)
}

export const exportFinancialTransactionItems = async (
  projectId: string,
  visibleColumns?: string[]
) => {
  const params: { project: string; visible_columns?: string } = {
    project: projectId
  }
  if (visibleColumns && visibleColumns.length > 0) {
    params.visible_columns = visibleColumns.join(",")
  }
  return request.api.get<Blob, BaseApiResponse<unknown>>(
    PATHS.exportFinancialTransactionItems,
    params,
    { responseType: "blob" }
  )
}

export const createFinancialTransactionItem = async (
  data: FinancialTransactionItemData
) => {
  return request.api.post<
    BaseApiResponse<FinancialTransactionItemResponse>,
    BaseApiResponse<unknown>
  >(PATHS.createFinancialTransactionItem, data)
}

export const getFinancialTransactionItemsFilterOptions = async (
  params: FinancialTransactionFilterOptionsParams
) => {
  return request.api.get<
    FinancialTransactionFilterOptions,
    BaseApiResponse<unknown>
  >(PATHS.getFinancialTransactionItemsFilterOptions, params)
}

export const getFinancialTransactionItem = async (id: string) => {
  return request.api.get<
    FinancialTransactionItemResponse,
    BaseApiResponse<unknown>
  >(PATHS.getFinancialTransactionItem(id))
}

export const updateFinancialTransactionItem = async (
  id: string,
  data: FinancialTransactionItemUpdateData
) => {
  return request.api.put<
    BaseApiResponse<FinancialTransactionItemResponse>,
    BaseApiResponse<unknown>
  >(PATHS.updateFinancialTransactionItem(id), data)
}

export const patchFinancialTransactionItem = async ({
  id,
  ...data
}: FinancialTransactionItemPatchData) => {
  return request.api.patch<
    BaseApiResponse<FinancialTransactionItemResponse>,
    BaseApiResponse<unknown>
  >(PATHS.patchFinancialTransactionItem(id), data)
}

export const deleteFinancialTransactionItem = async (id: string) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.deleteFinancialTransactionItem(id)
  )
}

export const getFinancialSnapshot = async (id: string) => {
  return request.api.get<FinancialSnapshotResponse, BaseApiResponse<unknown>>(
    PATHS.getFinancialSnapshot(id)
  )
}

export const addPayment = async (
  transactionId: string,
  body: PaymentCreateBody
) => {
  return request.api.post<BaseApiResponse<PaymentResponse>, BaseApiResponse<unknown>>(
    PATHS.addPayment(transactionId),
    body
  )
}

export const updatePayment = async (
  transactionId: string,
  paymentId: string,
  body: PaymentUpdateBody
) => {
  return request.api.patch<BaseApiResponse<PaymentResponse>, BaseApiResponse<unknown>>(
    PATHS.paymentDetail(transactionId, paymentId),
    body
  )
}

export const deletePayment = async (
  transactionId: string,
  paymentId: string
) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.paymentDetail(transactionId, paymentId)
  )
}

export const getFinancialAlerts = async (id: string) => {
  return request.api.get<
    BaseApiResponse<unknown>,
    BaseApiResponse<unknown>
  >(PATHS.getFinancialAlerts(id))
}

export const muteFinancialAlerts = async (id: string, muted: boolean) => {
  return request.api.post<
    BaseApiResponse<unknown>,
    BaseApiResponse<unknown>
  >(PATHS.muteFinancialAlerts(id), { muted })
}
